import { useEffect, useRef, useState } from 'react';

export const useScrollAnimation = (threshold = 0.1) => {
    const ref = useRef<HTMLElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Graceful fallback for environments without IntersectionObserver (older
        // crawlers, snapshot tools, full-page screenshot capture). Without this,
        // content below the fold renders as opacity-0 to bots and tools.
        if (typeof IntersectionObserver === 'undefined') {
            setIsVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            // Negative bottom rootMargin would over-delay; use a generous
            // positive margin so animations trigger slightly before the
            // section enters the viewport.
            { threshold, rootMargin: '0px 0px 200px 0px' }
        );

        const currentRef = ref.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        // Safety: if the element never gets observed (e.g. prerender or
        // captureBeyondViewport-style screenshots that don't trigger IO),
        // reveal after a short delay so content is still visible.
        const fallback = window.setTimeout(() => setIsVisible(true), 800);

        return () => {
            window.clearTimeout(fallback);
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [threshold]);

    return { ref, isVisible };
};
